import { SesionDetalleResponse } from "./response/SesionDetalleResponse";

/**
 * Informe de una semana completa: la misma forma que el detalle de una sesión
 * —para que el documento del reporte lo consuma sin cambios— más el rango de
 * fechas y cuántos recorridos se fusionaron.
 */
export type InformeSemanal = SesionDetalleResponse & {
  /** Fecha del primer recorrido de la semana (`yyyy-MM-dd`). */
  fechaDesde: string;
  /** Fecha del último recorrido (coincide con `fecha`). */
  fechaHasta: string;
  /** Cuántas sesiones se fusionaron en este informe. */
  recorridos: number;
};

/**
 * Fusiona los detalles de todas las sesiones de una semana en un solo informe.
 *
 * El monitoreo de una semana se reparte en varias salidas —unos lotes el lunes,
 * otros el miércoles—, así que el informe de una sesión suelta muestra solo los
 * lotes de ESA salida: de ahí el "Lotes monitoreados: 1" en una finca con
 * cinco. El informe semanal es el que responde a la pregunta real, que es qué
 * se encontró en la finca esa semana.
 *
 * Reglas de combinación:
 * - **Lotes**: unión por `loteId`. Si un lote se recorrió dos veces en la
 *   semana vale el **último**, igual que en el gráfico (`agruparPorSemana`), y
 *   se reemplaza el registro entero, no campo por campo.
 * - **Monitor y agrónomo**: pueden ser distintos por salida, así que se listan
 *   todos los nombres distintos separados por coma.
 * - **`isFinalizada`**: solo si **todas** las sesiones lo están. Si alguna
 *   quedó abierta, el informe se marca como no finalizado — sus datos pueden
 *   estar incompletos.
 * - **`fecha`**: la del último recorrido, que es la que da nombre al archivo
 *   PDF; el rango completo viaja aparte en `fechaDesde`/`fechaHasta`.
 * - **`puntos`**: se **concatenan**, no "último gana" como los lotes — cada
 *   punto es un árbol de una salida concreta, no un valor que una salida más
 *   reciente pueda corregir. `ordenados` ya está en orden cronológico de
 *   salida y cada `puntos` interno ya viene ordenado por `fechaCaptura`
 *   (server), así que concatenar alcanza para mantener el orden global — sin
 *   esto, el `{...ultimo}` de abajo dejaría solo los puntos del último día,
 *   el mismo bug que este fusionado existe para evitar con los lotes.
 *

 * @param detalles Detalles de las sesiones de una misma semana (en cualquier
 *                 orden; se ordenan por fecha antes de fusionar).
 * @returns El informe fusionado, o `null` si no se pasó ningún detalle.
 */
export function fusionarInformeSemanal(
  detalles: SesionDetalleResponse[],
): InformeSemanal | null {
  if (detalles.length === 0) return null;

  const ordenados = [...detalles].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const ultimo = ordenados[ordenados.length - 1];

  // Unión de lotes: recorrer en orden cronológico deja el último recorrido de
  // cada lote como el vigente.
  const lotesPorId = new Map<string, SesionDetalleResponse["lotes"][number]>();
  for (const detalle of ordenados) {
    for (const lote of detalle.lotes) lotesPorId.set(lote.id, lote);
  }

  return {
    ...ultimo,
    monitor: _unirNombres(ordenados.map((d) => d.monitor)),
    agronomo: _unirNombres(ordenados.map((d) => d.agronomo)),
    isFinalizada: ordenados.every((d) => d.isFinalizada),
    lotes: [...lotesPorId.values()].sort((a, b) =>
      a.nombre.localeCompare(b.nombre),
    ),
    puntos: ordenados.flatMap((d) => d.puntos),
    fechaDesde: ordenados[0].fecha.slice(0, 10),
    fechaHasta: ultimo.fecha.slice(0, 10),
    recorridos: ordenados.length,
  };
}

/** Une nombres distintos y no vacíos con coma; `null` si no hay ninguno. */
function _unirNombres(nombres: (string | null)[]): string | null {
  const unicos = [
    ...new Set(nombres.filter((nombre): nombre is string => !!nombre?.trim())),
  ];
  return unicos.length > 0 ? unicos.join(", ") : null;
}
